/** ProblemSolvers — asymmetric "Tetris" bento of the heaviest-use features,
 *  each framed as the real problem it kills. Grid-border style, no shadows. */
import { motion } from "framer-motion";
import {
  Camera, ThermometerSun, Landmark, MapPin, CreditCard, ScanLine, ArrowRight,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const CELLS = [
  {
    key: "instant-quote", icon: Camera, accent: "cyan", span: "lg:col-span-4 lg:row-span-2",
    problem: "\u201cCan I get a price?\u201d texts eating your whole evening",
    title: "Photo Intake \u2192 Instant Quote Range",
    body: "Customers fill a 2-minute form with car photos, pick a real package from YOUR menu, and instantly see a price range pulled from your pricing guide — size, add-ons, and travel included. You review the photos and send the final quote for one-tap approval.",
    stat: ["2 min", "from link to priced lead"],
  },
  {
    key: "heat", icon: ThermometerSun, accent: "orange", span: "lg:col-span-4",
    problem: "Summer jobs cooking your crew (and your schedule)",
    title: "Heat Safety & Weather Command",
    body: "Per-job forecasts, NIOSH-based risk levels, cooler-slot suggestions inside customer booking on scorcher days, one-tap worker check-ins with a guided 911 flow, and hydration supply forecasting.",
    stat: ["LOW\u2192EXTREME", "live risk on every job"],
  },
  {
    key: "payroll", icon: Landmark, accent: "emerald", span: "lg:col-span-4",
    problem: "Payroll + compliance paperwork stealing your Sundays",
    title: "50-State Payroll + I-9 Vault",
    body: "W-2 and 1099 in one run — tip credit, OT, PTO, year-end W-2/1099 downloads, and a guided I-9 onboarding checklist with a secure compliance vault.",
    stat: ["50 states", "one payroll engine"],
  },
  {
    key: "dispatch", icon: MapPin, accent: "blue", span: "lg:col-span-4",
    problem: "\u201cWhere's my guy?\u201d and 40 wasted miles a day",
    title: "GPS Time Clock + Route Optimize",
    body: "GPS-verified clock-ins, TomTom one-click route optimization with live ETAs, and a Field Tab that runs the whole day from the driveway — offline queue included.",
    stat: ["12 stops", "sequenced in one tap"],
  },
  {
    key: "paynow", icon: CreditCard, accent: "violet", span: "lg:col-span-4",
    problem: "Chasing checks weeks after the wax dried",
    title: "Invoices with Stripe Pay-Now",
    body: "Any job becomes a branded invoice with a Pay Now link — sent by text or email. Overdue reminders fire on autopilot until it's paid.",
    stat: ["1 tap", "job \u2192 paid invoice"],
  },
  {
    key: "scanners", icon: ScanLine, accent: "cyan", span: "lg:col-span-4",
    problem: "Inventory counts and receipt piles nobody has time for",
    title: "AI Camera Scanners",
    body: "Point your camera at a receipt, a case of water, or a gas pump slip — AI vision stocks inventory, prices units, tracks cost per job, and builds your weekly restock shopping list.",
    stat: ["1 photo", "inventory + costs done"],
  },
];

const ACCENT = {
  cyan: "group-hover:border-cyan-400/60 text-cyan-300",
  orange: "group-hover:border-orange-400/60 text-orange-300",
  emerald: "group-hover:border-emerald-400/60 text-emerald-300",
  blue: "group-hover:border-blue-400/60 text-blue-300",
  violet: "group-hover:border-violet-400/60 text-violet-300",
};

export const ProblemSolvers = () => {
  const nav = useNavigate();
  return (
    <section id="solutions" className="max-w-7xl mx-auto px-4 md:px-6 py-24 border-t border-white/5 scroll-mt-20">
      <div className="max-w-3xl mb-12">
        <span className="uppercase text-xs tracking-[0.2em] font-bold text-cyan-300/80" data-testid="solutions-eyebrow">
          The stuff that actually eats your week
        </span>
        <h2 className="mt-3 font-heading text-4xl md:text-5xl font-black tracking-tight">
          Six problems.<br /><span className="text-[var(--ld-royal-hover)]">Killed on day one.</span>
        </h2>
      </div>
      <div className="grid lg:grid-cols-12 gap-4">
        {CELLS.map((c, i) => {
          const I = c.icon;
          const a = ACCENT[c.accent];
          return (
            <motion.div key={c.key}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ delay: (i % 3) * 0.12, duration: 0.5 }}
              className={`group rounded-xl border border-white/10 bg-[var(--ld-surface,#141414)] p-6 flex flex-col transition-colors hover:-translate-y-1 duration-300 ${a} ${c.span}`}
              data-testid={`solution-card-${c.key}`}>
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider font-bold text-rose-300/90">
                <span className="w-1.5 h-1.5 rounded-full bg-rose-400 shrink-0" />
                {c.problem}
              </div>
              <div className="mt-4 flex items-center gap-2.5">
                <I size={20} className="shrink-0" />
                <h3 className="font-heading text-xl md:text-2xl font-bold text-white tracking-tight">{c.title}</h3>
              </div>
              <p className="mt-3 text-sm text-slate-400 leading-relaxed flex-1">{c.body}</p>
              <div className="mt-5 pt-4 border-t border-white/5 flex items-end justify-between gap-3">
                <div>
                  <div className="font-heading text-2xl font-black text-white tabular-nums">{c.stat[0]}</div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500">{c.stat[1]}</div>
                </div>
                <button onClick={() => nav("/register")}
                  className="text-[11px] font-semibold inline-flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity"
                  data-testid={`solution-cta-${c.key}`}>
                  Try it free <ArrowRight size={12} />
                </button>
              </div>
            </motion.div>
          );
        })}
      </div>
    </section>
  );
};

export default ProblemSolvers;
